import { ColDef } from '@ag-grid-community/core';
import { AgGridReact } from '@ag-grid-community/react';
import { Flex, Input, useBreakpointValue, Text, Badge } from '@chakra-ui/react';
import { useMemo, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { usePhysician } from '../../hooks/usePhysician';
import { CreatePhysicianDto, PhysicianDto, SearchPhysicianDto } from '../../lib/ServerApi';
import {
    DialogActionTrigger,
    DialogBody,
    DialogCloseTrigger,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Field, FieldGroup, FieldRow } from '../ui/field';
import { Tag } from '../ui/tag';
import { Grid } from '../ui/grid';
import { PaginationNextTrigger, PaginationPrevTrigger, PaginationRoot, PaginationPageText } from '../ui/pagination';
import { Combobox } from '../ui/combobox';
import { states } from '@/utils/coding-details';
import { GridFilterActionBar } from '@/components/ui/grid-filter-bar';
import { EMPTY_GRID_STATE, usePersistedGridState } from '@/hooks/usePersistedGridState';

const PAGE_SIZE = 20;

export const PhysicianModal = ({
    isOpen,
    onClose,
    onAdd,
    initialSearch,
}: {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (physicianNPI: string) => void;
    /** Search to start with, e.g. the name typed into a physician picker that found no match. */
    initialSearch?: Pick<SearchPhysicianDto, 'npiNumber' | 'firstName' | 'lastName'>;
}) => {
    const { data, isFetching, setSearchCriteria, mutate, clearCache, gotoPage, page, hasNextPage } = usePhysician({
        onAddSuccess: (physicianId: string) => {
            onAdd(physicianId);
            clearCache();
            physicianModalRhf.reset();
        },
        pageSize: PAGE_SIZE,
    });

    // Responsive breakpoints
    const isMobile = useBreakpointValue({ base: true, md: false, lg: false });
    const isTablet = useBreakpointValue({ base: false, md: true, lg: false });
    const gridRef = useRef<AgGridReact<PhysicianDto & { actions: React.ReactElement }>>(null);
    const { gridState, saveGridState, applyGridState } = usePersistedGridState('physicianSearchGrid', EMPTY_GRID_STATE);

    const handleOnClose = () => {
        physicianModalRhf.reset();
        clearCache();
        onClose();
    };

    const physicianModalRhf = useForm<SearchPhysicianDto>({
        defaultValues: {
            npiNumber: '',
            firstName: '',
            lastName: '',
            state: '',
            city: '',
            specialty: '',
            ...initialSearch,
        },
        reValidateMode: 'onBlur',
    });

    useEffect(() => {
        if (isOpen) {
            setSearchCriteria({
                npiNumber: '',
                firstName: '',
                lastName: '',
                state: '',
                city: '',
                specialty: '',
                ...initialSearch,
            });
        }
        // initialSearch only seeds the first search when the modal opens, so it is not a dependency.
        // setSearchCriteria is stable, so this runs once per open.
    }, [isOpen, initialSearch, setSearchCriteria]);

    const colDefs = useMemo<ColDef<PhysicianDto & { actions: React.ReactElement }>[]>(() => {
        if (isMobile) {
            return [
                {
                    headerName: 'Physician',
                    width: 180,
                    minWidth: 180,
                    maxWidth: 180,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: false,
                    resizable: true,
                    sortable: false,
                    pinned: 'left',
                    lockPinned: true,
                    cellRenderer: ({ data }: { data: PhysicianDto }) => (
                        <Flex direction="column" py="2" height="100%" justify="center">
                            <Text fontSize="sm" fontWeight="400" lineHeight="1" mb="1">
                                {data.firstName} {data.lastName}
                            </Text>
                            <Flex alignItems="center" gap="2" mb="1">
                                <Text fontSize="xs" color="gray.600" lineHeight="1">
                                    NPI: {data.npi}
                                </Text>
                                <Badge colorPalette={data.pecos ? 'green' : 'red'} variant="subtle" size="sm">
                                    <Text fontSize="10px" color={data.pecos ? 'green.800' : 'red.800'} fontWeight={500}>
                                        {data.pecos ? 'Enrolled' : 'Not Enrolled'}
                                    </Text>
                                </Badge>
                            </Flex>
                        </Flex>
                    ),
                },
                {
                    field: 'credential',
                    headerName: 'Cred',
                    width: 80,
                    minWidth: 80,
                    maxWidth: 80,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: false,
                    resizable: true,
                },
                {
                    field: 'city',
                    headerName: 'City',
                    width: 120,
                    minWidth: 120,
                    maxWidth: 120,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: false,
                    resizable: true,
                },
                {
                    field: 'state',
                    headerName: 'State',
                    width: 80,
                    minWidth: 80,
                    maxWidth: 80,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: false,
                    resizable: true,
                },
                {
                    headerName: 'Action',
                    width: 80,
                    minWidth: 80,
                    maxWidth: 80,
                    suppressHeaderFilterButton: true,
                    resizable: false,
                    sortable: false,
                    pinned: false,
                    lockPinned: true,
                    cellRenderer: ({ data }: { data: CreatePhysicianDto }) => (
                        <Flex alignItems="center" height="100%" justify="center">
                            <Button type="button" size="xs" onClick={() => mutate(data)}>
                                Add
                            </Button>
                        </Flex>
                    ),
                },
            ];
        }

        if (isTablet) {
            return [
                {
                    field: 'npi',
                    headerName: 'NPI',
                    width: 120,
                    minWidth: 120,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: false,
                    resizable: false,
                    pinned: 'left',
                    lockPinned: true,
                },
                {
                    field: 'firstName',
                    headerName: 'First',
                    flex: 1,
                    minWidth: 100,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: false,
                    resizable: true,
                },
                {
                    field: 'lastName',
                    headerName: 'Last',
                    flex: 1,
                    minWidth: 100,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: false,
                    resizable: true,
                    sort: 'desc',
                },
                {
                    field: 'credential',
                    headerName: 'Cred',
                    flex: 1,
                    minWidth: 80,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: false,
                    resizable: false,
                },
                {
                    field: 'city',
                    flex: 1,
                    minWidth: 100,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: false,
                    resizable: true,
                },
                {
                    field: 'pecos',
                    headerName: 'PECOS',
                    flex: 1,
                    minWidth: 150,
                    filter: 'agTextColumnFilter',
                    suppressHeaderFilterButton: true,
                    resizable: true,
                    cellRenderer: ({ data }: { data: PhysicianDto }) => {
                        return (
                            <Flex flex={1} alignItems="center" height="100%">
                                <Tag colorPalette={data.pecos ? 'green' : 'red'} textTransform="uppercase" fontWeight="700">
                                    {data.pecos ? 'enrolled' : 'not enrolled'}
                                </Tag>
                            </Flex>
                        );
                    },
                },
                {
                    headerName: 'Action',
                    width: 100,
                    minWidth: 100,
                    maxWidth: 100,
                    suppressHeaderFilterButton: true,
                    resizable: false,
                    sortable: false,
                    pinned: 'right',
                    lockPinned: true,
                    cellRenderer: ({ data }: { data: CreatePhysicianDto }) => {
                        return (
                            <Button type="button" size="sm" onClick={() => mutate(data)}>
                                Add
                            </Button>
                        );
                    },
                },
            ];
        }

        return [
            {
                field: 'npi',
                headerName: 'NPI',
                flex: 1,
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: false,
                resizable: true,
                minWidth: 120,
            },
            {
                field: 'firstName',
                headerName: 'First',
                flex: 1,
                minWidth: 120,
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: false,
                resizable: true,
            },
            {
                field: 'lastName',
                headerName: 'Last',
                flex: 1,
                minWidth: 120,
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: false,
                resizable: true,
                sort: 'desc',
            },
            {
                field: 'credential',
                headerName: 'Cred',
                flex: 1,
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: false,
                resizable: true,
                minWidth: 80,
            },
            { field: 'city', flex: 1, minWidth: 120, filter: 'agTextColumnFilter', suppressHeaderFilterButton: false, resizable: true },
            { field: 'state', flex: 1, minWidth: 100, filter: 'agTextColumnFilter', suppressHeaderFilterButton: false, resizable: false },
            {
                field: 'pecos',
                headerName: 'PECOS',
                flex: 1,
                minWidth: 140,
                filter: 'agTextColumnFilter',
                suppressHeaderFilterButton: true,
                resizable: false,
                cellRenderer: ({ data }: { data: PhysicianDto }) => {
                    return (
                        <Flex flex={1} alignItems="center" height="100%">
                            <Tag colorPalette={data.pecos ? 'green' : 'red'} textTransform="uppercase" fontWeight="700">
                                {data.pecos ? 'enrolled' : 'not enrolled'}
                            </Tag>
                        </Flex>
                    );
                },
            },
            {
                flex: 1,
                suppressHeaderFilterButton: true,
                resizable: false,
                minWidth: 140,
                cellRenderer: ({ data }: { data: CreatePhysicianDto }) => {
                    return (
                        <Button type="button" size="sm" onClick={() => mutate(data)}>
                            Add
                        </Button>
                    );
                },
            },
        ];
    }, [isMobile, isTablet, mutate]);

    const stateItems = useMemo(() => states.map((state) => ({ value: state, label: state })), []);

    return (
        <DialogRoot size={isMobile ? 'full' : 'xl'} lazyMount open={isOpen} onOpenChange={() => handleOnClose()}>
            <DialogContent colorPalette="purple" maxW={{ base: '100%', md: '90%', lg: '60%' }} w="100%" h={{ base: '100vh', md: 'auto' }}>
                <form
                    id="physician-search"
                    noValidate
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return physicianModalRhf.handleSubmit(setSearchCriteria)(e);
                    }}>
                    <DialogHeader>
                        <DialogTitle>Physician Search</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        <FieldGroup>
                            {isMobile ? (
                                <>
                                    <Field label="NPI">
                                        <Input {...physicianModalRhf.register('npiNumber')} />
                                    </Field>
                                    <Flex gap="2">
                                        <Field label="First name" flex={1}>
                                            <Input {...physicianModalRhf.register('firstName')} />
                                        </Field>
                                        <Field label="Last name" flex={1}>
                                            <Input {...physicianModalRhf.register('lastName')} />
                                        </Field>
                                    </Flex>
                                    <Flex gap="2">
                                        <Field label="City" flex={1}>
                                            <Input {...physicianModalRhf.register('city')} />
                                        </Field>
                                        <Field label="State" w="100px">
                                            <Controller
                                                control={physicianModalRhf.control}
                                                name="state"
                                                render={({ field: { onChange, value, name } }) => {
                                                    return (
                                                        <Combobox
                                                            items={stateItems}
                                                            name={name}
                                                            onChange={(value) => onChange(value)}
                                                            value={stateItems.find((item) => item.value === value)}
                                                            renderItem={(item) => item.label}
                                                            portalled={false}
                                                        />
                                                    );
                                                }}
                                            />
                                        </Field>
                                    </Flex>
                                </>
                            ) : (
                                <FieldRow>
                                    <Field label="NPI">
                                        <Input {...physicianModalRhf.register('npiNumber')} />
                                    </Field>
                                    <Field label="First name">
                                        <Input {...physicianModalRhf.register('firstName')} />
                                    </Field>
                                    <Field label="Last name">
                                        <Input {...physicianModalRhf.register('lastName')} />
                                    </Field>
                                    <Field label="City">
                                        <Input {...physicianModalRhf.register('city')} />
                                    </Field>
                                    <Field label="State">
                                        <Controller
                                            control={physicianModalRhf.control}
                                            name="state"
                                            render={({ field: { onChange, value, name } }) => {
                                                return (
                                                    <Combobox
                                                        items={stateItems}
                                                        name={name}
                                                        onChange={(value) => onChange(value)}
                                                        value={stateItems.find((item) => item.value === value)}
                                                        renderItem={(item) => item.label}
                                                        portalled={false}
                                                    />
                                                );
                                            }}
                                        />
                                    </Field>
                                </FieldRow>
                            )}
                        </FieldGroup>
                        <Flex mt="4" flexDirection="column" style={{ width: '100%', height: '50vh' }}>
                            <GridFilterActionBar gridRef={gridRef} filterModel={gridState.filterModel} colDefs={colDefs} />
                            <Grid<PhysicianDto & { actions: React.ReactElement }>
                                countLabel="physician"
                                key={isMobile ? 'mobile-grid' : isTablet ? 'tablet-grid' : 'desktop-grid'}
                                gridRef={gridRef}
                                suppressRowHoverHighlight
                                columnDefs={colDefs}
                                rowData={data as (PhysicianDto & { actions: React.ReactElement })[]}
                                loading={isFetching}
                                rowHeight={isMobile ? 75 : undefined}
                                suppressHorizontalScroll={false}
                                enableRangeSelection={false}
                                onGridReady={(event) => applyGridState(event.api)}
                                onFilterChanged={(event) => saveGridState(event.api)}
                                onSortChanged={(event) => saveGridState(event.api)}
                            />
                        </Flex>
                    </DialogBody>
                    <DialogFooter>
                        <Flex direction={{ base: 'column', md: 'row' }} align="center" justify="space-between" w="100%" gap="3">
                            <PaginationRoot
                                count={hasNextPage ? PAGE_SIZE * page + 1 : PAGE_SIZE * page}
                                pageSize={PAGE_SIZE}
                                page={page}
                                onPageChange={(details) => {
                                    gotoPage(details.page);
                                }}>
                                <Flex alignItems="center" gap="16px">
                                    <PaginationPrevTrigger />
                                    <PaginationPageText format="page" />
                                    <PaginationNextTrigger />
                                </Flex>
                            </PaginationRoot>

                            <Flex gap="2" w={{ base: '100%', md: 'auto' }}>
                                <DialogActionTrigger asChild>
                                    <Button variant="surface" onClick={() => handleOnClose()} flex={{ base: 1, md: 'none' }}>
                                        Cancel
                                    </Button>
                                </DialogActionTrigger>
                                <Button
                                    form="physician-search"
                                    type="submit"
                                    loading={isFetching}
                                    loadingText="Searching"
                                    flex={{ base: 1, md: 'none' }}>
                                    Search
                                </Button>
                            </Flex>
                        </Flex>
                    </DialogFooter>
                    <DialogCloseTrigger />
                </form>
            </DialogContent>
        </DialogRoot>
    );
};
